import { CustomFields } from "@investorRequest/models";
import {
  DetectedPaymentMethod,
  ExtractionConfidence,
  ExtractionResult,
  PaymentRail,
  ResolvedExtractionFields,
} from "@investorRequest/models/bankExtraction";
import { getFormFieldEntries, SWIFT_EXTRACTION_TO_FORM_FIELD } from "@investorRequest/utils/extractionFieldMapping";

const EXTRACTION_FORM_FIELD_KEYS = Object.values(SWIFT_EXTRACTION_TO_FORM_FIELD);

export interface ApplyExtractionResultOutput {
  customFields: Partial<CustomFields>;
  originalExtractedValues: Record<string, string | null>;
  fieldConfidence: Record<string, ExtractionConfidence>;
}

export interface ApplyExtractionOptions {
  achDisabled?: boolean;
}

const pickResolvedRail = (
  fieldsByRail: ExtractionResult["fieldsByRail"],
  rail: PaymentRail
): ResolvedExtractionFields | undefined => {
  switch (rail) {
    case "ach": {
      const fields = fieldsByRail.ach;

      return fields ? { rail: "ach", fields } : undefined;
    }
    case "fedwire": {
      const fields = fieldsByRail.fedwire;

      return fields ? { rail: "fedwire", fields } : undefined;
    }
    case "swift": {
      const fields = fieldsByRail.swift;

      return fields ? { rail: "swift", fields } : undefined;
    }
    default:
      return undefined;
  }
};

export const resolveExtractionFields = (
  result: ExtractionResult,
  paymentMethod: DetectedPaymentMethod
): ResolvedExtractionFields | undefined => {
  const { fieldsByRail, detectedPaymentMethod } = result;

  if (paymentMethod !== "unknown") {
    return pickResolvedRail(fieldsByRail, paymentMethod);
  }

  if (detectedPaymentMethod !== "unknown") {
    return pickResolvedRail(fieldsByRail, detectedPaymentMethod);
  }

  return (
    pickResolvedRail(fieldsByRail, "ach") ??
    pickResolvedRail(fieldsByRail, "fedwire") ??
    pickResolvedRail(fieldsByRail, "swift")
  );
};

export const reapplyExtractionForPaymentMethod = (
  result: ExtractionResult,
  paymentMethod: DetectedPaymentMethod,
  fieldDefaults: Partial<CustomFields>
): ApplyExtractionResultOutput => {
  const customFields: Partial<CustomFields> = { paymentMethod };
  const originalExtractedValues: Record<string, string | null> = {};
  const fieldConfidence: Record<string, ExtractionConfidence> = {};

  EXTRACTION_FORM_FIELD_KEYS.forEach((formKey) => {
    const defaultValue = fieldDefaults[formKey as keyof CustomFields];

    if (defaultValue !== undefined) {
      customFields[formKey] = defaultValue;
    }
  });

  const railFields = resolveExtractionFields(result, paymentMethod);

  if (!railFields) {
    return { customFields, originalExtractedValues, fieldConfidence };
  }

  getFormFieldEntries(railFields).forEach(({ formKey, field }) => {
    fieldConfidence[formKey] = field.confidence;
    originalExtractedValues[formKey] = field.value;

    if (field.confidence !== "missing" && field.value != null) {
      customFields[formKey] = field.value;
    }
  });

  return { customFields, originalExtractedValues, fieldConfidence };
};

export const applyExtractionResult = (
  result: ExtractionResult,
  existingCustomFields: CustomFields,
  options?: ApplyExtractionOptions
): ApplyExtractionResultOutput => {
  const customFields: Partial<CustomFields> = { ...existingCustomFields };
  const originalExtractedValues: Record<string, string | null> = {};
  const fieldConfidence: Record<string, ExtractionConfidence> = {};

  if (result.detectedPaymentMethod !== "unknown") {
    if (result.detectedPaymentMethod === "ach" && options?.achDisabled) {
      customFields.paymentMethod = "fedwire";
    } else {
      customFields.paymentMethod = result.detectedPaymentMethod;
    }
  }

  const paymentMethod = (customFields.paymentMethod ||
    result.detectedPaymentMethod ||
    "unknown") as DetectedPaymentMethod;
  const railFields = resolveExtractionFields(result, paymentMethod);

  if (!railFields) {
    return { customFields, originalExtractedValues, fieldConfidence };
  }

  getFormFieldEntries(railFields).forEach(({ formKey, field }) => {
    fieldConfidence[formKey] = field.confidence;
    originalExtractedValues[formKey] = field.value;

    if (field.confidence !== "missing" && field.value != null) {
      customFields[formKey] = field.value;
    }
  });

  return { customFields, originalExtractedValues, fieldConfidence };
};

export const isTotalExtractionFailure = (
  result: ExtractionResult,
  paymentMethod: DetectedPaymentMethod = "unknown"
): boolean => {
  const fields = resolveExtractionFields(result, paymentMethod);

  if (!fields) {
    return true;
  }

  return getFormFieldEntries(fields).every(({ field }) => field.confidence === "missing");
};

export const paymentMethodLabel = (method: string): string => {
  switch (method) {
    case "ach":
      return "ACH";
    case "fedwire":
      return "Fedwire";
    case "swift":
      return "SWIFT";
    default:
      return method;
  }
};
